import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import LoadingSquare from "shared/components/loading/LoadingSquare";

import {
  Chart as ChartJS,
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import "chartjs-adapter-date-fns";

import { addDate, substractDate } from "shared/utils/date.js";

ChartJS.register(CategoryScale, TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const getDateKey = (date) => {
  const d = new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);

  return d;
};

const ChartLinePricingHistory = ({ pricings, history, timeUnit }) => {
  const rootStyles = getComputedStyle(document.documentElement);

  const primaryColorRgb = rootStyles.getPropertyValue("--primary-color-rgb").trim();

  const [chartData, setChartData] = useState(null);
  const [ageChangeMarkers, setAgeChangeMarkers] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  /*
   * Initialize visible chart range.
   *
   * Important:
   * endDate is set to the END of today rather than the current
   * time, otherwise a point normalized to today's midnight can
   * sometimes be excluded depending on date/time handling.
   */
  useEffect(() => {
    if (history && pricings) {
      const start = substractDate(new Date(), timeUnit);

      setStartDate(startOfDay(start));
      setEndDate(endOfDay(new Date()));
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [history, pricings, timeUnit]);

  /*
   * Build chart data.
   */
  useEffect(() => {
    if (!pricings || !history || !startDate || !endDate || history.length === 0 || pricings.length === 0) {
      return;
    }

    const sortedHistory = [...history].sort((a, b) => new Date(a.date) - new Date(b.date));

    const sortedPricings = [...pricings].sort((a, b) => new Date(a.date) - new Date(b.date));

    /*
     * Group history by local calendar day.
     */
    const historyMap = {};

    sortedHistory.forEach((item) => {
      const day = getDateKey(item.date);

      if (!historyMap[day]) {
        historyMap[day] = [];
      }

      historyMap[day].push(item);
    });

    /*
     * Group pricings by local calendar day.
     */
    const pricingMap = {};

    sortedPricings.forEach((item) => {
      const day = getDateKey(item.date);

      if (!pricingMap[day]) {
        pricingMap[day] = [];
      }

      pricingMap[day].push(item);
    });

    const pricePoints = [];
    const overallChangePoints = [];
    const ageChanges = [];

    /*
     * Find the first date available in either dataset.
     */
    const firstHistoryDate = new Date(sortedHistory[0].date);

    const firstPricingDate = new Date(sortedPricings[0].date);

    const firstDate = firstHistoryDate < firstPricingDate ? firstHistoryDate : firstPricingDate;

    /*
     * Find the latest date available in either dataset.
     *
     * This is important because pricing data may contain
     * today's data even when history does not.
     */
    const lastHistoryDate = new Date(sortedHistory[sortedHistory.length - 1].date);

    const lastPricingDate = new Date(sortedPricings[sortedPricings.length - 1].date);

    const lastDataDate = new Date(Math.max(lastHistoryDate.getTime(), lastPricingDate.getTime()));

    /*
     * Always allow the chart to reach the end of today,
     * but don't cut off data that might be newer.
     */
    const today = endOfDay(new Date());

    const lastDate = today > lastDataDate ? today : endOfDay(lastDataDate);

    let currentOverall = null;
    let currentAge = null;
    let lastPrice = null;

    /*
     * Iterate one calendar day at a time.
     */
    for (let d = startOfDay(firstDate); d <= lastDate; d.setDate(d.getDate() + 1)) {
      const currentDate = new Date(d);
      const dayStr = getDateKey(currentDate);

      const dayHistory = historyMap[dayStr] || [];

      let overallChanged = false;

      /*
       * Apply all history changes for this day.
       */
      dayHistory.forEach((item) => {
        if (item.values?.overall !== undefined) {
          currentOverall = item.values.overall;
          overallChanged = true;
        }

        if (item.values?.age !== undefined) {
          currentAge = item.values.age;

          ageChanges.push({
            date: new Date(currentDate),
            age: currentAge,
          });
        }
      });

      /*
       * Find the pricing matching the current overall
       * and age for this day.
       */
      const dayPricings = pricingMap[dayStr] || [];

      const match = dayPricings.find((item) => item.overall === currentOverall && item.age === currentAge);

      /*
       * Update the current price if a matching pricing
       * entry exists.
       */
      if (match) {
        lastPrice = match.price;
      }

      /*
       * Add the price point.
       */
      if (lastPrice !== null) {
        pricePoints.push({
          x: new Date(currentDate),
          y: lastPrice,
          overall: currentOverall,
          age: currentAge,
        });
      }

      /*
       * Add a marker when the overall changed.
       */
      if (match && overallChanged && lastPrice !== null) {
        overallChangePoints.push({
          x: new Date(currentDate),
          y: lastPrice,
          overall: currentOverall,
          age: currentAge,
        });
      }
    }

    /*
     * Keep only markers visible in the current range.
     */
    setAgeChangeMarkers(ageChanges.filter((marker) => marker.date >= startDate && marker.date <= endDate));

    /*
     * Set chart datasets.
     */
    setChartData({
      datasets: [
        {
          label: "Price",
          fill: true,
          borderColor: `rgb(${primaryColorRgb})`,
          backgroundColor: `rgba(${primaryColorRgb}, .4)`,

          data: pricePoints.filter((point) => point.x >= startDate && point.x <= endDate),

          borderWidth: 1,
          pointRadius: 2,
          pointHoverRadius: 4,
        },

        {
          label: "OVR +1",

          data: overallChangePoints.filter((point) => point.x >= startDate && point.x <= endDate),

          borderColor: "white",
          backgroundColor: "white",
          pointRadius: 5,
          showLine: false,
        },
      ],
    });
  }, [history, pricings, startDate, endDate, primaryColorRgb]);

  /*
   * Navigation controls.
   */
  const extendLeft = () => {
    setStartDate((prev) => prev && startOfDay(substractDate(prev, timeUnit)));
  };

  const extendRight = () => {
    setEndDate((prev) => prev && endOfDay(addDate(prev, timeUnit)));
  };

  const moveLeft = () => {
    setStartDate((prev) => prev && startOfDay(substractDate(prev, timeUnit)));

    setEndDate((prev) => prev && endOfDay(substractDate(prev, timeUnit)));
  };

  const moveRight = () => {
    setStartDate((prev) => prev && startOfDay(addDate(prev, timeUnit)));

    setEndDate((prev) => prev && endOfDay(addDate(prev, timeUnit)));
  };

  /*
   * Draw the Overall change marker.
   */
  const drawOVRPlugin = {
    id: "drawOVRPlugin",

    afterDatasetsDraw: (chart) => {
      const { ctx, scales, data } = chart;

      const datasetIndex = data.datasets.findIndex((dataset) => dataset.label === "OVR +1");

      if (datasetIndex === -1) {
        return;
      }

      const dataset = data.datasets[datasetIndex];

      ctx.save();

      dataset.data.forEach((point) => {
        const x = scales.x.getPixelForValue(point.x);

        const y = scales.y.getPixelForValue(point.y);

        /*
         * Draw white circle.
         */
        ctx.beginPath();

        ctx.arc(x, y, 6, 0, 2 * Math.PI);

        ctx.fillStyle = "white";
        ctx.fill();

        ctx.strokeStyle = `rgb(${primaryColorRgb})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        /*
         * Draw Overall value inside circle.
         */
        ctx.fillStyle = "black";
        ctx.font = "8px Saira";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        ctx.fillText(point.overall, x, y);
      });

      ctx.restore();
    },
  };

  /*
   * Draw vertical age change markers.
   */
  const ageLinePlugin = {
    id: "ageLinePlugin",

    afterDatasetsDraw: (chart) => {
      if (!ageChangeMarkers || ageChangeMarkers.length === 0) {
        return;
      }

      const { ctx, scales } = chart;

      ageChangeMarkers.forEach((marker) => {
        const x = scales.x.getPixelForValue(marker.date);

        ctx.save();

        /*
         * Draw vertical line.
         */
        ctx.beginPath();

        ctx.moveTo(x, scales.y.top);

        ctx.lineTo(x, scales.y.bottom);

        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(255,255,255,.2)";
        ctx.stroke();

        /*
         * Draw age label.
         */
        ctx.fillStyle = "rgba(255,255,255,.2)";

        ctx.font = "10px Saira";

        ctx.textAlign = "center";

        ctx.fillText(`${marker.age}`, x + 9, scales.y.bottom - 4);

        ctx.restore();
      });
    },
  };

  /*
   * Chart configuration.
   */
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "x",

    scales: {
      x: {
        type: "time",

        grid: {
          display: false,
          drawBorder: false,
        },

        border: {
          display: false,
        },

        min: startDate,
        max: endDate,

        time: {
          unit: "day",

          tooltipFormat: "dd-MM",

          displayFormats: {
            day: "dd-MM",
            month: "MMM yyyy",
          },
        },
      },

      y: {
        ticks: {
          beginAtZero: true,
          stepSize: 1,
          maxTicksLimit: 5,
        },

        grid: {
          display: false,
          drawBorder: false,
        },

        border: {
          display: false,
        },
      },
    },

    plugins: {
      legend: {
        display: true,

        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 15,
          boxWidth: 20,
        },
      },

      datalabels: {
        display: false,
      },

      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset.label === "Price") {
              const point = context.raw;

              return [`Price: ${point.y}`, `Overall: ${point.overall}`, `Age: ${point.age}`];
            }

            return `${context.dataset.label}: ${context.formattedValue}`;
          },
        },
      },
    },
  };

  return (
    <div className="position-relative h-100 w-100">
      {!chartData ? (
        <LoadingSquare />
      ) : (
        <div className="h-100 w-100">
          <div className="position-absolute top-0 start-0">
            <button className="btn btn-small" onClick={extendLeft}>
              <i className="bi bi-arrow-bar-left text-main" />
            </button>

            <button className="btn btn-small" onClick={moveLeft}>
              <i className="bi bi-caret-left-fill text-main" />
            </button>
          </div>

          <div className="position-absolute top-0 end-0">
            <button className="btn btn-small" onClick={moveRight}>
              <i className="bi bi-caret-right-fill text-main" />
            </button>

            <button className="btn btn-small" onClick={extendRight}>
              <i className="bi bi-arrow-bar-right text-main" />
            </button>
          </div>

          <div className="h-100 w-100">
            <Line data={chartData} options={options} plugins={[ageLinePlugin, drawOVRPlugin]} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartLinePricingHistory;
